import { useQuery } from '@apollo/client'
import { AppBar, Tab, Tabs, useTheme } from '@material-ui/core'
import { useMemo } from 'react'
import { Redirect, useParams } from 'react-router-dom'

import { buildFeatureSet, canAccessTab, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import { TENANT_NAME_QUERY } from '../../graphql/tenant'
import { TabPanel } from '../../helpers/TabPanel'
import { parseTenantCode } from '../../helpers/tenantCode'
import { getTranslate, lang } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import tenantPagesRoleManagement from '../../staticRoleManagement/tenantRoleManagement'
import { Tenant } from '../../types/tenants'
import TenantBookingPermission from '../TenantBookingPermission'
import TenantDataExport from '../TenantDataExport'
import TenantForm from '../TenantForm'
import { UploadBoxForTenantLogo } from '../TenantUpload'
import useStyles from './styles/index.styles'

const TenantTabs = () => {
  const classes = useStyles()
  const theme = useTheme()
  const { tenantId, tenantCode, page } = useParams<{
    tenantId: string
    tenantCode: string
    page: string
  }>()
  const parsedTenantCode = parseTenantCode(tenantCode)

  const { data, loading } = useQuery<{ tenantName: Tenant }>(
    TENANT_NAME_QUERY,
    {
      variables: {
        tenantCode: parsedTenantCode ?? 0
      },
      skip: parsedTenantCode === null
    }
  )
  const cachedLevels = useMemo(
    () => [
      { label: getTranslate('Tenants'), link: '/tenant' },
      {
        label: data?.tenantName.basicDetails.name || ''
      }
    ],
    [data]
  )

  const tabsList = [
    {
      disableRipple: true,
      value: 'details',
      className: classes.tab,
      label: getTranslate('Details'),
      link: `/tenant/${tenantId}/${tenantCode}/details`,
      component: LocalizedLink
    },
    {
      disableRipple: false,
      value: 'logo',
      className: classes.tab,
      label: getTranslate('Logo'),
      link: `/tenant/${tenantId}/${tenantCode}/logo`,
      component: LocalizedLink
    },
    {
      disableRipple: false,
      value: 'booking-permission',
      className: classes.tab,
      label: getTranslate('Booking Permission'),
      link: `/tenant/${tenantId}/${tenantCode}/booking-permission`,
      component: LocalizedLink
    },
    {
      disableRipple: false,
      value: 'data-export',
      className: classes.tab,
      label: getTranslate('Data Export'),
      link: `/tenant/${tenantId}/${tenantCode}/data-export`,
      component: LocalizedLink
    }
  ]

  const credentials = getCredentials()
  const featureSet = buildFeatureSet(credentials)

  const routeConfig = tenantPagesRoleManagement.find(
    (item) => item.route === '/tenant/:tenantId/:tenantCode/:page'
  )

  const visibleTabs = tabsList.filter((tab) =>
    canAccessTab(routeConfig, tab.value, featureSet)
  )

  if (!canAccessTab(routeConfig, page, featureSet)) {
    return <Redirect to="/403" />
  }

  if (parsedTenantCode === null) {
    return <Redirect to={`/${lang}/tenant`} />
  }

  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar} position="static" color="transparent">
        <Breadcrumbs levels={cachedLevels} loading={loading} />
        <Tabs
          value={page}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto">
          {visibleTabs.map((tabItem) => (
            <Tab
              key={tabItem.value}
              disableRipple={tabItem.disableRipple}
              value={tabItem.value}
              className={tabItem.className}
              label={tabItem.label}
              to={tabItem.link}
              component={LocalizedLink}
            />
          ))}
        </Tabs>
      </AppBar>
      <TabPanel
        value={page}
        index="details"
        dir={theme.direction}
        className={classes.tabPanel}>
        <TenantForm />
      </TabPanel>
      <TabPanel
        value={page}
        index="logo"
        dir={theme.direction}
        className={classes.tabPanel}>
        <UploadBoxForTenantLogo />
      </TabPanel>
      <TabPanel
        value={page}
        index="booking-permission"
        dir={theme.direction}
        className={classes.tabPanel}>
        <TenantBookingPermission />
      </TabPanel>
      <TabPanel
        value={page}
        index="data-export"
        dir={theme.direction}
        className={classes.tabPanel}>
        <TenantDataExport />
      </TabPanel>
    </div>
  )
}
export default TenantTabs
